import NiceModal from "@ebay/nice-modal-react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useEffect } from "react";

import { Sidebar } from "@/app/layout/Sidebar";
import { useMenuItems } from "@/app/layout/Sidebar/use-menu-items";
import { useCurrentUser } from "@/core/hooks/use-current-user";
import { fetchWithTimeout } from "@/core/lib/api-client";
import {
  apiErrorData,
  extractApiExceptionCodeFromData,
  isApiError,
} from "@/core/lib/api-errors";
import { saveAuthRedirect } from "@/core/lib/auth-redirect";
import { setFaroUser } from "@/core/lib/observability";
import InvitationCard from "@/core/ui/InvitationCard";
import LoadingScreen from "@/core/ui/layout/LoadingScreen";
import { DashboardProvider } from "@/features/dashboard/components/context/dashboard-provider";
import type { AppInfo, UserRead, InvitationRead } from "@/openapi";
import {
  getCurrentUserInvitationsOptions,
  getCurrentUserOptions,
  infoOptions,
} from "@/openapi/@tanstack/react-query.gen";

function mapAuthError(err: unknown, target: string): never {
  if (isApiError(err)) {
    const code = extractApiExceptionCodeFromData(apiErrorData(err) ?? null);
    switch (code) {
      case "USER_NOT_VERIFIED":
        throw redirect({ to: "/auth/request-verify", replace: true });
      case "FAILED_TO_VERIFY_TWO_FA":
        saveAuthRedirect(target);
        throw redirect({
          to: "/auth/two-fa-verify",
          replace: true,
        });
      case "TWO_FA_NEEDS_REGENERATION":
        saveAuthRedirect(target);
        throw redirect({ to: "/auth/two-fa", search: { regenerate: true } });
      case "TWO_FA_NOT_ENABLED":
        saveAuthRedirect(target);
        throw redirect({ to: "/auth/two-fa" });
      case "TERMS_NOT_ACCEPTED":
        saveAuthRedirect(target);
        throw redirect({ to: "/auth/terms", replace: true });
    }
  }

  throw err;
}

export const Route = createFileRoute("/_protected")({
  component: App,
  pendingComponent: () => <LoadingScreen isActive noDim />,
  pendingMs: 100,
  beforeLoad: async ({
    context: { queryClient },
    location,
  }): Promise<{
    user: UserRead;
    appInfo: AppInfo;
    invitation?: InvitationRead;
  }> => {
    try {
      // revalidate membership instead of serving it from cache, so a stale /me
      // (persisted or long-lived in-memory) can't keep the sidebar and route
      // guards on privileges the user no longer holds; staleTime bounds the
      // refetch so this doesn't hit the network on every in-app navigation
      const user = await queryClient.query({
        ...getCurrentUserOptions({ fetch: fetchWithTimeout(5_000) }),
        staleTime: 30_000,
      });
      const [appInfo, invitations] = await Promise.all([
        queryClient.query({ ...infoOptions(), staleTime: "static" }),
        queryClient.query({
          ...getCurrentUserInvitationsOptions(),
          staleTime: "static",
        }),
      ]);

      if (invitations.length > 0) {
        return { user, appInfo, invitation: invitations[0] };
      }

      if (
        user.organization === null &&
        location.pathname !== "/no-organization"
      ) {
        throw redirect({ to: "/no-organization" });
      }

      return { user, appInfo };
    } catch (err) {
      mapAuthError(err, location.pathname + location.searchStr);
    }
  },
  loader: ({ context }) => {
    return {
      user: context.user,
      appInfo: context.appInfo,
    };
  },
});

function App() {
  const { invitation } = Route.useRouteContext();
  const user = useCurrentUser();
  const menuItems = useMenuItems();

  useEffect(() => {
    setFaroUser({ id: user.id });
  }, [user.id]);

  return (
    <NiceModal.Provider>
      <div className="flex size-full min-h-0 flex-row items-stretch">
        <Sidebar menuItems={menuItems} className="float-left" />
        <div className="bg-background relative flex min-h-0 min-w-0 flex-1 flex-col">
          {invitation ? (
            <InvitationCard
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              id={invitation.id}
              organization={invitation.organization}
            />
          ) : (
            <DashboardProvider>
              <Outlet />
            </DashboardProvider>
          )}
        </div>
      </div>
    </NiceModal.Provider>
  );
}
